/**
 * Web Push enablement for the operator PWA (OCP-331). Wraps the
 * Notification permission + PushManager subscription dance and syncs
 * the subscription to the backend (/v1/admin/push/*).
 */
import { api } from './api';

export interface PushStatus {
  /** Browser supports SW + PushManager + Notification. */
  supported: boolean;
  permission: NotificationPermission;
  /** This device has an active push subscription. */
  subscribed: boolean;
  /** Backend has VAPID keys (push can actually be sent). */
  configured: boolean;
}

interface VapidResponse {
  public_key: string | null;
  configured: boolean;
}

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getPushStatus(): Promise<PushStatus> {
  if (!pushSupported()) {
    return { supported: false, permission: 'default', subscribed: false, configured: false };
  }
  let configured = false;
  try {
    configured = (await api<VapidResponse>('/v1/admin/push/vapid-public-key')).configured;
  } catch {
    configured = false;
  }
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: Boolean(sub),
    configured,
  };
}

/** Request permission, subscribe, and persist to the backend. */
export async function enablePush(): Promise<void> {
  if (!pushSupported()) throw new Error('push_unsupported');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission_denied');

  const vapid = await api<VapidResponse>('/v1/admin/push/vapid-public-key');
  if (!vapid.configured || !vapid.public_key) throw new Error('push_not_configured');

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast: TS lib types Uint8Array as Uint8Array<ArrayBufferLike>, which
    // doesn't narrow to BufferSource even though it is an ArrayBufferView.
    applicationServerKey: urlBase64ToUint8Array(vapid.public_key) as BufferSource,
  });
  const json = sub.toJSON();
  await api('/v1/admin/push/subscribe', {
    method: 'POST',
    body: {
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      user_agent: navigator.userAgent.slice(0, 400),
    },
  });
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await api('/v1/admin/push/subscribe', { method: 'DELETE', body: { endpoint: sub.endpoint } }).catch(
    () => {},
  );
  await sub.unsubscribe();
}

export async function sendTestPush(): Promise<void> {
  await api('/v1/admin/push/test', { method: 'POST' });
}
